/**
 * Broadcast create/edit — one component for both `new` and `:id/edit`,
 * switched on whether `useParams().id` is present (same shape as
 * PropertyFormPage/TenantFormPage/LeaseFormPage). Manager roles only.
 * `sender` is forced server-side to the caller; sending is a documented no-op
 * beyond creating/updating the row (no FCM push fires) — see the backend
 * route file — so editing an already-"sent" broadcast is not the awkward
 * case it would be in a real push system.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { createBroadcast, getBroadcast, updateBroadcast } from '../../api/endpoints/messaging';
import { BROADCAST_TARGET_GROUPS, type BroadcastTargetGroup } from '../../types/messaging';

const INITIAL_FORM = {
  message: '',
  target_group: 'ALL_TENANTS' as BroadcastTargetGroup,
  property: '',
};

type FormState = typeof INITIAL_FORM;

export function BroadcastFormPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const broadcastId = Number(id);
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const existing = useQuery({
    queryKey: ['broadcast', broadcastId],
    queryFn: () => getBroadcast(broadcastId),
    enabled: isEdit && Number.isFinite(broadcastId),
  });

  useEffect(() => {
    if (existing.data === undefined) return;
    const broadcast = existing.data;
    setForm({
      message: broadcast.message,
      target_group: broadcast.target_group,
      property: broadcast.property === null ? '' : String(broadcast.property),
    });
  }, [existing.data]);

  function update(field: keyof FormState, value: string): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        message: form.message,
        target_group: form.target_group,
        property: form.property === '' ? null : Number(form.property),
      };
      return isEdit ? updateBroadcast(broadcastId, payload) : createBroadcast(payload);
    },
    onSuccess: () => navigate('..', { replace: true }),
    onError: (caught) => {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  if (isEdit && existing.isPending) return <p>Loading…</p>;
  if (isEdit && existing.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(existing.error).message}
      </p>
    );
  }

  return (
    <section>
      <h2>{isEdit ? 'Edit broadcast' : 'New broadcast'}</h2>

      <form onSubmit={handleSubmit} noValidate>
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <label htmlFor="broadcast-message">Message</label>
        <textarea
          id="broadcast-message"
          required
          value={form.message}
          onChange={(event) => update('message', event.target.value)}
        />
        {fieldError('message') !== undefined && (
          <p className="form__field-error">{fieldError('message')}</p>
        )}

        <label htmlFor="broadcast-target-group">Target group</label>
        <select
          id="broadcast-target-group"
          value={form.target_group}
          onChange={(event) => update('target_group', event.target.value)}
        >
          {BROADCAST_TARGET_GROUPS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <label htmlFor="broadcast-property">Property id (optional)</label>
        <input
          id="broadcast-property"
          type="number"
          value={form.property}
          onChange={(event) => update('property', event.target.value)}
        />

        <button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : isEdit ? 'Save' : 'Send broadcast'}
        </button>
      </form>
    </section>
  );
}

export default BroadcastFormPage;
